import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';

/**
 * Session Timeout Dialog
 *
 * Displays a countdown warning when the user's session is about to expire due to inactivity.
 * - Shows when there are 3 minutes remaining before token expiry AND user has been inactive
 * - Displays accurate countdown synced to actual token expiration time
 * - Allows user to continue session (refreshes token)
 * - User MUST click "Continue Working" - activity alone won't dismiss the dialog
 * - Syncs across all tabs via BroadcastChannel
 * - Redirects to login if countdown reaches 0
 *
 * Visual design: matches rocket-ui component patterns (gray palette, system fonts,
 * card-style border/shadow, Chakra-compatible button styling).
 */

// ---------------------------------------------------------------------------
// Design tokens (Chakra UI v3 gray / zinc scale)
// ---------------------------------------------------------------------------
const gray = {
  50: '#fafafa',
  100: '#f4f4f5',
  200: '#e4e4e7',
  300: '#d4d4d8',
  400: '#a1a1aa',
  500: '#71717a',
  600: '#52525b',
  700: '#3f3f46',
  800: '#27272a',
  900: '#18181b',
  950: '#09090b',
};

const FONT =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';
const MONO = 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace';

// ---------------------------------------------------------------------------
// Injected keyframes (can't be expressed as inline styles)
// ---------------------------------------------------------------------------
const STYLE_ID = 'bb-timeout-dialog-css';

const ensureKeyframes = (nonce) => {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;

  // Stamp the CSP nonce so the injected <style> is allowed by
  // Content-Security-Policy. The nonce is passed down from SessionManager.
  if (nonce) {
    el.setAttribute('nonce', nonce);
  }

  el.textContent = [
    '@keyframes bbTdOverlayIn{from{opacity:0}to{opacity:1}}',
    '@keyframes bbTdDialogIn{from{opacity:0;transform:translateY(6px) scale(.98)}to{opacity:1;transform:translateY(0) scale(1)}}',
    '@keyframes bbTdPulse{0%,100%{opacity:1}50%{opacity:.45}}',
  ].join('');
  document.head.appendChild(el);
};

// ---------------------------------------------------------------------------
// Static styles
// ---------------------------------------------------------------------------
const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    animation: 'bbTdOverlayIn 0.15s ease-out',
  },
  dialog: {
    maxWidth: '420px',
    width: '92%',
    backgroundColor: '#ffffff',
    border: `1px solid ${gray[200]}`,
    borderRadius: '8px',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.08), 0 2px 4px -2px rgba(0,0,0,0.06)',
    textAlign: 'center',
    fontFamily: FONT,
    animation: 'bbTdDialogIn 0.2s ease-out',
    overflow: 'hidden',
  },
  body: {
    padding: '32px 32px 24px',
  },
  iconCircle: {
    width: '48px',
    height: '48px',
    margin: '0 auto 20px',
    backgroundColor: gray[100],
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    color: gray[900],
    margin: '0 0 8px',
    lineHeight: 1.3,
  },
  description: {
    fontSize: '14px',
    fontWeight: 400,
    color: gray[500],
    margin: 0,
    lineHeight: 1.6,
  },
  countdownWrapper: {
    margin: '24px 0 0',
  },
  countdown: {
    fontSize: '40px',
    fontWeight: 600,
    fontFamily: MONO,
    color: gray[800],
    lineHeight: 1,
    letterSpacing: '0.02em',
  },
  countdownUrgent: {
    animation: 'bbTdPulse 1.5s ease-in-out infinite',
  },
  countdownLabel: {
    fontSize: '12px',
    fontWeight: 500,
    color: gray[400],
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginTop: '6px',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    padding: '16px 24px',
    borderTop: `1px solid ${gray[100]}`,
    backgroundColor: gray[50],
  },
  // Base button (shared)
  btn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '36px',
    padding: '0 16px',
    fontSize: '14px',
    fontWeight: 600,
    fontFamily: FONT,
    borderRadius: '6px',
    cursor: 'pointer',
    border: 'none',
    outline: 'none',
    transition: 'background-color 0.15s, border-color 0.15s, box-shadow 0.15s',
    whiteSpace: 'nowrap',
  },
  // Outline variant (Log out)
  btnOutline: {
    backgroundColor: 'transparent',
    color: gray[600],
    border: `1px solid ${gray[300]}`,
  },
  btnOutlineHover: {
    backgroundColor: gray[50],
    borderColor: gray[400],
  },
  // Solid variant (Continue working)
  btnSolid: {
    backgroundColor: gray[800],
    color: '#ffffff',
  },
  btnSolidHover: {
    backgroundColor: gray[700],
  },
  btnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  // Focus ring
  btnFocusRing: {
    boxShadow: `0 0 0 2px #fff, 0 0 0 4px ${gray[400]}`,
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format seconds into M:SS display */
const formatCountdown = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

/** Parse "M:SS" string to total seconds (for urgency detection in Auth0 mode) */
const parseFormattedTime = (str) => {
  if (!str) return Infinity;
  const parts = str.split(':');
  if (parts.length !== 2) return Infinity;
  return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
};

// ---------------------------------------------------------------------------
// Icon
// ---------------------------------------------------------------------------
const ClockIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke={gray[500]}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export const TimeoutDialog = ({
  isOpen,
  expirationTimestamp,
  timeRemainingFormatted,
  onContinue,
  onLogout,
  isRefreshing = false,
  nonce,
}) => {
  const [secondsRemaining, setSecondsRemaining] = useState(120);
  const [continueHover, setContinueHover] = useState(false);
  const [continueFocus, setContinueFocus] = useState(false);
  const [logoutHover, setLogoutHover] = useState(false);
  const [logoutFocus, setLogoutFocus] = useState(false);
  const timerRef = useRef(null);

  // Inject keyframe CSS on first render
  useEffect(() => ensureKeyframes(nonce), [nonce]);

  /**
   * Calculate seconds remaining from expiration timestamp.
   * Syncs to real time to handle JS timer drift.
   */
  const calculateSecondsRemaining = useCallback(() => {
    if (!expirationTimestamp) return 0;
    return Math.max(0, Math.floor((expirationTimestamp - Date.now()) / 1000));
  }, [expirationTimestamp]);

  /**
   * Countdown timer — only active when expirationTimestamp is provided (OIDC mode).
   * Auth0 mode supplies timeRemainingFormatted directly.
   */
  useEffect(() => {
    if (timeRemainingFormatted !== undefined) return;

    if (!isOpen) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    setSecondsRemaining(calculateSecondsRemaining());

    timerRef.current = setInterval(() => {
      const remaining = calculateSecondsRemaining();
      setSecondsRemaining(remaining);
      if (remaining <= 0) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        onLogout?.();
      }
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [
    isOpen,
    expirationTimestamp,
    timeRemainingFormatted,
    calculateSecondsRemaining,
    onLogout,
  ]);

  // Re-sync countdown when tab regains visibility
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible' && isOpen) {
        setSecondsRemaining(calculateSecondsRemaining());
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [isOpen, calculateSecondsRemaining]);

  if (!isOpen) return null;

  // Determine urgency level for visual feedback
  const effectiveSeconds =
    timeRemainingFormatted !== undefined
      ? parseFormattedTime(timeRemainingFormatted)
      : secondsRemaining;
  const isUrgent = effectiveSeconds <= 30;

  const displayTime =
    timeRemainingFormatted !== undefined
      ? timeRemainingFormatted
      : formatCountdown(secondsRemaining);

  const handleContinueClick = () => {
    if (!isRefreshing) onContinue?.();
  };

  return (
    <div style={styles.overlay} onClick={(e) => e.stopPropagation()}>
      <div
        style={styles.dialog}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="session-timeout-title"
        aria-describedby="session-timeout-desc"
      >
        {/* Body */}
        <div style={styles.body}>
          <div style={styles.iconCircle}>
            <ClockIcon />
          </div>

          <h2 id="session-timeout-title" style={styles.title}>
            Session expiring
          </h2>

          <p id="session-timeout-desc" style={styles.description}>
            Your session will expire due to inactivity.
            <br />
            Click &ldquo;Continue&rdquo; to stay logged in.
          </p>

          <div style={styles.countdownWrapper}>
            <div
              style={{
                ...styles.countdown,
                ...(isUrgent ? styles.countdownUrgent : null),
              }}
              aria-live="polite"
              aria-atomic="true"
            >
              {displayTime}
            </div>
            <div style={styles.countdownLabel}>time remaining</div>
          </div>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button
            type="button"
            onClick={() => onLogout?.()}
            style={{
              ...styles.btn,
              ...styles.btnOutline,
              ...(logoutHover ? styles.btnOutlineHover : null),
              ...(logoutFocus ? styles.btnFocusRing : null),
            }}
            onMouseEnter={() => setLogoutHover(true)}
            onMouseLeave={() => setLogoutHover(false)}
            onFocus={() => setLogoutFocus(true)}
            onBlur={() => setLogoutFocus(false)}
          >
            Log out
          </button>
          <button
            type="button"
            onClick={handleContinueClick}
            disabled={isRefreshing}
            style={{
              ...styles.btn,
              ...styles.btnSolid,
              ...(continueHover && !isRefreshing ? styles.btnSolidHover : null),
              ...(continueFocus ? styles.btnFocusRing : null),
              ...(isRefreshing ? styles.btnDisabled : null),
            }}
            onMouseEnter={() => setContinueHover(true)}
            onMouseLeave={() => setContinueHover(false)}
            onFocus={() => setContinueFocus(true)}
            onBlur={() => setContinueFocus(false)}
          >
            {isRefreshing ? 'Refreshing\u2026' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

TimeoutDialog.propTypes = {
  /** Whether the dialog is visible */
  isOpen: PropTypes.bool.isRequired,
  /** Unix timestamp (ms) when the token expires (OIDC mode) */
  expirationTimestamp: PropTypes.number,
  /** Pre-formatted time remaining string e.g. "2:30" (Auth0 mode) */
  timeRemainingFormatted: PropTypes.string,
  /** Called when user clicks "Continue" */
  onContinue: PropTypes.func,
  /** Called when countdown reaches 0 or user clicks "Log out" */
  onLogout: PropTypes.func,
  /** Whether token refresh is in progress */
  isRefreshing: PropTypes.bool,
  /** CSP nonce for runtime-injected <style> tags */
  nonce: PropTypes.string,
};

// Alias for backward compatibility
const SessionTimeoutDialog = TimeoutDialog;

export default SessionTimeoutDialog;
